import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { Button, message, Modal, Space, Spin, Typography } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';

import { claimTicketGiftByCode, getTicketGiftByCode } from '../../service/ticketGift';
import { My } from '../my';

type PageStatus = 'idle' | 'already-used' | 'already-holding' | 'expired' | 'not-found' | 'error';

interface TicketGiftError {
    errorType?: string;
}

interface StatusContent {
    title: string;
    description: string;
    buttonText: string;
    destination: string;
}

const statusContent: Record<Exclude<PageStatus, 'idle' | 'error'>, StatusContent> = {
    'already-used': {
        title: '이미 사용된 링크입니다.',
        description: '요가 패스 등록이 완료되어 이 링크는 다시 사용할 수 없습니다.',
        buttonText: '홈으로',
        destination: '/',
    },
    'already-holding': {
        title: '이미 등록된 요가 패스입니다.',
        description: '마이페이지에서 이용 정보를 확인하실 수 있습니다.',
        buttonText: '요가 패스 보기',
        destination: '/my',
    },
    expired: {
        title: '등록 기간이 지났습니다.',
        description: '이 요가 패스는 등록 기간이 지나 사용하실 수 없습니다.',
        buttonText: '홈으로',
        destination: '/',
    },
    'not-found': {
        title: '유효하지 않은 링크입니다.',
        description: '링크 주소가 정확한지 확인해 주세요.',
        buttonText: '홈으로',
        destination: '/',
    },
};

const formatExpireDate = (value: string): string => new Date(value).toLocaleDateString('ko-KR', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
});

const getErrorStatus = (error: unknown): PageStatus => {
    const errorType = (error as AxiosError<TicketGiftError>).response?.data?.errorType;
    if (errorType === 'GIFT_NOT_FOUND') return 'not-found';
    if (errorType === 'GIFT_ALREADY_USED') return 'already-used';
    if (errorType === 'ALREADY_HOLDING_TICKET') return 'already-holding';
    if (errorType === 'GIFT_EXPIRED') return 'expired';
    return 'error';
};

const TicketGift = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { code } = useParams() as { code: string };
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [pageStatus, setPageStatus] = useState<PageStatus>('idle');

    const ticketGiftQuery = useQuery({
        queryKey: ['ticketGift', code],
        queryFn: () => getTicketGiftByCode(code),
        select: (res) => res.data,
        enabled: Boolean(code),
        retry: false,
    });

    const claimMutation = useMutation({
        mutationFn: () => claimTicketGiftByCode(code),
        onSuccess: async (res) => {
            setIsModalOpen(false);
            message.success('요가 패스가 등록되었습니다');
            // 배경의 <My />가 등록 전에 시작한 조회가 아직 데이터를 못 받았으면 invalidate만으로는
            // 그 요청을 취소하지 못하고 재사용한다. 등록 전 목록이 캐시에 남아 새 정기권을 못 찾는다.
            await queryClient.cancelQueries({ queryKey: ['issuedWellnessTicket'] });
            queryClient.invalidateQueries({ queryKey: ['issuedWellnessTicket'] });
            navigate('/my', { state: { focusIssuedWellnessTicketId: res.data.issuedWellnessTicketId } });
        },
        onError: (error) => {
            setIsModalOpen(false);
            setPageStatus(getErrorStatus(error));
        },
    });

    useEffect(() => {
        if (!ticketGiftQuery.data) return;
        if (ticketGiftQuery.data.status === 'AVAILABLE') {
            setPageStatus('idle');
            setIsModalOpen(true);
        } else if (ticketGiftQuery.data.status === 'ALREADY_USED') {
            setPageStatus('already-used');
        } else if (ticketGiftQuery.data.status === 'EXPIRED') {
            setPageStatus('expired');
        } else {
            setPageStatus('already-holding');
        }
    }, [ticketGiftQuery.data]);

    useEffect(() => {
        if (ticketGiftQuery.isError) {
            setPageStatus(getErrorStatus(ticketGiftQuery.error));
        }
    }, [ticketGiftQuery.error, ticketGiftQuery.isError]);

    const retry = () => {
        setPageStatus('idle');
        if (ticketGiftQuery.data?.status === 'AVAILABLE') {
            claimMutation.mutate();
        } else {
            ticketGiftQuery.refetch();
        }
    };

    if (ticketGiftQuery.isPending) {
        return (
            <div style={{ minHeight: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (pageStatus === 'error') {
        return (
            <div style={{ minHeight: '70vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, textAlign: 'center' }}>
                <Typography.Title level={3}>요가 패스를 등록하지 못했습니다.</Typography.Title>
                <Typography.Paragraph>잠시 후 다시 시도해 주세요.</Typography.Paragraph>
                <Space>
                    <Button type="primary" onClick={retry} loading={claimMutation.isPending}>다시 시도</Button>
                    <Button onClick={() => navigate('/')}>홈으로</Button>
                </Space>
            </div>
        );
    }

    if (pageStatus !== 'idle') {
        const content = statusContent[pageStatus];
        return (
            <div style={{ minHeight: '70vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, textAlign: 'center' }}>
                <Typography.Title level={3}>{content.title}</Typography.Title>
                <Typography.Paragraph>{content.description}</Typography.Paragraph>
                <Button type="primary" onClick={() => navigate(content.destination)}>{content.buttonText}</Button>
            </div>
        );
    }

    const ticketGift = ticketGiftQuery.data;
    return (
        <>
            <My />
            <Modal
                open={isModalOpen}
                centered
                title="파지티브호텔 요가 패스를 등록하시겠어요?"
                okText="등록하기"
                cancelText="다음에"
                confirmLoading={claimMutation.isPending}
                onOk={() => claimMutation.mutate()}
                onCancel={() => {
                    if (claimMutation.isPending) return;
                    navigate('/');
                }}
                cancelButtonProps={{ disabled: claimMutation.isPending }}
                keyboard={!claimMutation.isPending}
                closable={false}
                maskClosable={false}
            >
                <Typography.Paragraph>{ticketGift?.wellnessTicketName}</Typography.Paragraph>
                <Typography.Paragraph>
                    {ticketGift?.expireDate
                        ? `${ticketGift?.totalUsableCnt}회 · ${formatExpireDate(ticketGift.expireDate)}까지`
                        : `${ticketGift?.totalUsableCnt}회 등록일로부터 ${ticketGift?.usableDate}일간 이용 가능합니다.`}
                </Typography.Paragraph>
            </Modal>
        </>
    );
};

export default TicketGift;
